import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Sparkles,
  Wand2,
  MonitorSmartphone,
  Banknote,
  FileText,
  Mail,
  Rss,
  LayoutDashboard,
  Layers,
  Users,
  LogIn,
  ShieldCheck,
  LifeBuoy,
} from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { MarketingPageHead } from '../MarketingPageHead';

/**
 * ChangelogPage
 * --------------------------------------------------------------
 * A real, hand-written product changelog — latest first.
 *
 * This is intentionally NOT driven by the blog CMS: the changelog
 * is release notes ("what shipped, does it affect me?"), which is a
 * different thing from the blog's operator essays. Editors add a new
 * entry to ENTRIES below when something ships.
 *
 * Subscribe form is a mailto for now — a real list will live behind
 * /api/v1/newsletter once email opt-in is wired up.
 */

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

type EntryTag = 'Launch' | 'Improvement' | 'Fix';

type Entry = {
  date: string;       // ISO yyyy-mm-dd
  title: string;
  tag: EntryTag;
  Icon: React.ComponentType<{ className?: string }>;
  body: string;
  bullets: string[];
};

const ENTRIES: Entry[] = [
  {
    date: '2026-07-17',
    title: 'Permission-aware dashboard + recovery everywhere',
    tag: 'Improvement',
    Icon: LayoutDashboard,
    body: 'Every module now respects each teammate\'s permissions with clear "no access" states instead of dead ends, and archived records can be restored instead of lost. A big correctness pass across the whole app.',
    bullets: [
      'Role-based access reflected across the entire dashboard',
      'Archive + restore for invoices, clients, projects, and expenses',
      'Invoice pagination + a dedicated credit-notes tab',
      'Employees can view their own payslips + attendance history',
    ],
  },
  {
    date: '2026-06-14',
    title: 'Deeper invoicing, payroll, projects & expenses',
    tag: 'Improvement',
    Icon: Layers,
    body: 'A depth pass across the modules — the features growing teams ask for once the basics are in place.',
    bullets: [
      'Invoicing: partial payments, discounts, estimates, credit notes, per-client currency',
      'Payroll: tax withholding, recurring runs, year-to-date, payslip config',
      'Projects: budgets, billable time, invoice-from-time, milestones',
      'Expenses: approval chains, mileage + per-diem, reimburse on payroll',
      'Email: campaigns + delivery analytics',
    ],
  },
  {
    date: '2026-06-11',
    title: 'Live screen view + team management',
    tag: 'Launch',
    Icon: Users,
    body: 'See a team member\'s screen live (fully opt-in), manage members and custom roles from Settings, and — for platform owners — a new metrics dashboard. The desktop agent gained a faster heartbeat for near-instant live view.',
    bullets: [
      'Live screen view with request + countdown (agent 1.1.0)',
      'Members list, custom roles + per-permission editor',
      'Invite teammates with a role straight from HR',
      'Platform-owner metrics + billing health',
    ],
  },
  {
    date: '2026-06-10',
    title: 'Sign in with Google',
    tag: 'Launch',
    Icon: LogIn,
    body: 'Skip the password. Tenant users can now sign in and sign up with a Google account — auto-linked by verified email, with a workspace picker for people who belong to more than one workspace.',
    bullets: [
      'One-tap Google sign-in on login + signup',
      'Auto-links to your existing workspace by verified email',
      'Workspace picker for multi-workspace accounts',
    ],
  },
  {
    date: '2026-06-04',
    title: 'Legal center + self-serve password reset',
    tag: 'Improvement',
    Icon: ShieldCheck,
    body: 'Terms, Privacy, DPA, and Acceptable-Use are now published, and anyone can reset their own password from the login screen — no support ticket required.',
    bullets: [
      'Terms · Privacy · DPA · Acceptable use pages',
      'Forgot-password flow linked from the login screen',
      'Per-module access controls across the dashboard',
    ],
  },
  {
    date: '2026-05-30',
    title: 'Help Center + Support tickets',
    tag: 'Launch',
    Icon: LifeBuoy,
    body: 'A searchable Help Center at /docs and a built-in support desk — customers open tickets from the dashboard or the public contact page, and every workspace\'s tickets stay isolated.',
    bullets: [
      'Help Center with guides + search at /docs',
      'Support tickets from inside the dashboard',
      'Public contact form with spam protection',
    ],
  },
  {
    date: '2026-05-22',
    title: 'Trial wizard + per-module pricing',
    tag: 'Launch',
    Icon: Wand2,
    body: 'A new in-product wizard turns the 7-day trial into a guided pick-modules-and-seats flow. The bill totals live as you toggle. Per-module + per-seat pricing is now the source of truth across signup, billing, and invoices.',
    bullets: [
      'Trial wizard with module + seat selection',
      'Live bill total as you toggle modules',
      'Card capture on the final step (Stripe SetupIntent)',
      'Pricing page mirrors the wizard\'s math',
    ],
  },
  {
    date: '2026-05-17',
    title: 'Worqly desktop agent for time tracking',
    tag: 'Launch',
    Icon: MonitorSmartphone,
    body: 'The Worqly Agent is live for Windows and macOS. It runs in the menu bar, tracks hours, optionally captures the screen and idle activity, and is fully opt-in at the employee level.',
    bullets: [
      'Installers for Windows · macOS',
      'Per-project timers with idle detection',
      'Optional screenshots + activity (employee approves)',
      'Super-admin release management + one-click install links',
    ],
  },
  {
    date: '2026-05-15',
    title: 'Platform billing + Stripe payouts',
    tag: 'Launch',
    Icon: Banknote,
    body: 'Subscriptions, invoices, and payment methods are now first-class in the dashboard. Workspaces can pay Worqly, and get paid by their own customers — all on the same billing surface.',
    bullets: [
      'Self-serve subscription management',
      'Module-level upgrade / downgrade with pro-ration',
      'Bring-your-own Stripe keys for customer payments',
      'Per-workspace billing history + invoice PDFs',
    ],
  },
  {
    date: '2026-05-12',
    title: 'Invoicing + Clients launch',
    tag: 'Launch',
    Icon: FileText,
    body: 'The flagship module — branded invoices, hosted Stripe pay links, recurring billing, automatic reminders, and a clients database that talks to every other module — is live.',
    bullets: [
      'Branded PDF invoices (logo + colors)',
      'Stripe-hosted pay links',
      'Recurring billing (monthly / quarterly / annual)',
      'Automatic 3-day + 7-day reminders',
      'Clients · services · settings pages',
    ],
  },
];

const TAG_TONE: Record<EntryTag, string> = {
  Launch:      'bg-coral text-white',
  Improvement: 'bg-lavender-100 text-lavender-700',
  Fix:         'bg-lime-neon text-ink',
};

export function ChangelogPage() {
  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title="Changelog"
        description="Every launch, improvement, and fix that ships in Worqly — with enough detail you can tell whether it affects you."
      />
      <Hero />
      <Timeline />
      <Subscribe />
      <FooterCTA />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * HERO
 * ───────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div initial="hidden" animate="visible" variants={STAGGER}>
          <motion.div variants={FADE_UP}>
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                bg-white/80 text-ink shadow-pill ring-1 ring-ink/5
                dark:bg-white/10 dark:text-white dark:ring-white/10 dark:shadow-none"
            >
              <Sparkles className="h-3 w-3 text-coral" />
              Product updates
            </span>
          </motion.div>

          <motion.h1
            variants={FADE_UP}
            className="mt-6 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.02] text-balance"
          >
            What's new in{' '}
            <span className="italic font-serif text-coral">Worqly.</span>
          </motion.h1>

          <motion.p
            variants={FADE_UP}
            className="mt-6 text-body-lg text-ink/65 dark:text-white/65 max-w-2xl mx-auto text-pretty"
          >
            Every launch, improvement, and fix — with enough detail you can tell whether it
            affects you. No marketing fluff, no "various improvements".
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * TIMELINE
 * ───────────────────────────────────────────────────────────── */

function Timeline() {
  return (
    <section className="py-16 lg:py-20 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.ol
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="space-y-5"
        >
          {ENTRIES.map((e, i) => (
            <motion.li key={e.date + e.title} variants={FADE_UP} className="relative">
              {i < ENTRIES.length - 1 && (
                <span
                  aria-hidden
                  className="absolute left-5 top-12 bottom-[-32px] w-px bg-ink/10 dark:bg-white/10"
                />
              )}

              <div className="flex items-start gap-4">
                <span className="relative z-10 inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral text-white shadow-cta shrink-0">
                  <e.Icon className="h-5 w-5" />
                </span>

                <div className="flex-1 min-w-0 rounded-3xl p-6 lg:p-7 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className={'inline-flex items-center px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.10em] ' + TAG_TONE[e.tag]}>
                      {e.tag}
                    </span>
                    <time
                      dateTime={e.date}
                      className="text-[12px] uppercase tracking-[0.14em] font-bold text-ink/55 dark:text-white/55 tabular-nums"
                    >
                      {formatDate(e.date)}
                    </time>
                  </div>

                  <h3 className="mt-3 text-[20px] font-semibold text-ink dark:text-white tracking-[-0.01em]">
                    {e.title}
                  </h3>
                  <p className="mt-2 text-[14.5px] text-ink/70 dark:text-white/70 leading-relaxed">
                    {e.body}
                  </p>

                  <ul className="mt-4 space-y-1.5">
                    {e.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2 text-[13.5px] text-ink/65 dark:text-white/65"
                      >
                        <span aria-hidden className="mt-2 inline-block h-1 w-1 rounded-full bg-coral shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.li>
          ))}
        </motion.ol>
      </div>
    </section>
  );
}

function formatDate(input: string): string {
  // Accept ISO date-only ("2026-05-22") or full timestamp.
  const dt = input.includes('T') ? new Date(input) : (() => {
    const [y, m, d] = input.split('-').map((s) => parseInt(s, 10));
    return new Date(Date.UTC(y, m - 1, d));
  })();
  return dt.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/* ─────────────────────────────────────────────────────────────
 * SUBSCRIBE
 * ───────────────────────────────────────────────────────────── */

function Subscribe() {
  const [email, setEmail] = React.useState('');

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const target = email.trim();
    if (!target) return;
    const subject = encodeURIComponent('Subscribe me to the Worqly changelog');
    const body = encodeURIComponent(
      `Please add ${target} to the Worqly changelog list. Thanks!`,
    );
    window.location.href = `mailto:hello@worqly.io?subject=${subject}&body=${body}`;
  }

  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={STAGGER}
          className="rounded-3xl bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card p-7 lg:p-10"
        >
          <motion.div variants={FADE_UP} className="flex items-start gap-4">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 shrink-0">
              <Rss className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <Eyebrow className="!text-coral">Stay in the loop</Eyebrow>
              <h2 className="mt-2 text-[24px] lg:text-[28px] font-semibold text-ink dark:text-white tracking-[-0.01em]">
                Get the changelog by email.
              </h2>
              <p className="mt-2 text-[14.5px] text-ink/65 dark:text-white/65 leading-relaxed">
                One email per launch — usually two per month. Unsubscribe at the bottom of any
                send. We don't share your address.
              </p>
            </div>
          </motion.div>

          <motion.form
            variants={FADE_UP}
            onSubmit={onSubmit}
            className="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-2 bg-pearl-50 dark:bg-white/[0.04] rounded-2xl p-2 ring-1 ring-ink/5 dark:ring-white/10"
          >
            <label htmlFor="changelog-email" className="sr-only">
              Email address
            </label>
            <div className="flex-1 flex items-center gap-2 px-3">
              <Mail className="h-4 w-4 text-ink/45 dark:text-white/45" />
              <input
                id="changelog-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="flex-1 h-11 bg-transparent text-[14px] text-ink dark:text-white placeholder:text-ink/40 dark:placeholder:text-white/40 outline-none"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-1.5 h-11 px-5 rounded-full bg-coral text-white text-[13.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
            >
              Subscribe
              <ArrowRight className="h-4 w-4" />
            </button>
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Footer CTA
 * ───────────────────────────────────────────────────────────── */

function FooterCTA() {
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">Start free</p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                The newest stuff is <em className="font-serif">already inside.</em>
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                Sign up and you'll find everything on this page — plus whatever ships next week.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink-900 text-white text-[14.5px] font-semibold hover:bg-black transition-colors"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/features"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/15 backdrop-blur-md text-white text-[14.5px] font-semibold ring-1 ring-white/25 hover:bg-white/25 transition-colors"
              >
                Tour features
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
